/**
 * Enhanced Error Context Provider
 * Global Multi-Tenant Error Management System
 *
 * @description Issue #283 対応 - グローバルエラーハンドリング強化
 * @backend NestJS ErrorResponse統合
 * @features トースト通知・エラー追跡・自動回復・分析レポート
 */

import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useEffect,
  ReactNode,
  useMemo,
} from 'react';

import {
  EnhancedApiError,
  handleError,
  logError,
  shouldLogoutOnError,
  shouldDisplayToUser,
  ErrorSeverity,
} from '../utils/error';
import logger from '../utils/logger';

// ====================================
// Error Context Types
// ====================================

export interface ErrorNotification {
  id: string;
  error: EnhancedApiError;
  timestamp: Date;
  isVisible: boolean;
  autoClose: boolean;
  duration: number;
  onRetry?: () => void;
  onDismiss?: () => void;
}

export interface ErrorContextState {
  // Active errors
  errors: ErrorNotification[];
  globalError: EnhancedApiError | null;

  // Error statistics
  errorCount: number;
  lastErrorTime: Date | null;

  // Configuration
  enableToasts: boolean;
  enableAutoRetry: boolean;
  enableAnalytics: boolean;

  // Status
  isHealthy: boolean;
  healthCheckInterval: number;
}

export interface ErrorContextActions {
  // Error Management
  reportError: (error: unknown, context?: Record<string, unknown>) => string;
  dismissError: (errorId: string) => void;
  retryError: (errorId: string) => void;
  clearAllErrors: () => void;

  // Global Error Handling
  setGlobalError: (error: EnhancedApiError | null) => void;

  // Configuration
  toggleToasts: (enabled: boolean) => void;
  toggleAutoRetry: (enabled: boolean) => void;
  toggleAnalytics: (enabled: boolean) => void;

  // Health & Analytics
  getErrorStats: () => ErrorStats;
  reportHealth: (isHealthy: boolean) => void;
}

export interface ErrorStats {
  totalErrors: number;
  errorsByType: Record<string, number>;
  errorsBySeverity: Record<ErrorSeverity, number>;
  averageErrorsPerDay: number;
  mostCommonErrors: Array<{ type: string; count: number }>;
  healthStatus: {
    isHealthy: boolean;
    lastCheck: Date | null;
    uptime: number;
  };
}

type ErrorContextValue = ErrorContextState & ErrorContextActions;

// ====================================
// Error Actions
// ====================================

type ErrorAction =
  | { type: 'REPORT_ERROR'; payload: { error: EnhancedApiError; id: string; onRetry?: () => void } }
  | { type: 'DISMISS_ERROR'; payload: { id: string } }
  | { type: 'RETRY_ERROR'; payload: { id: string } }
  | { type: 'CLEAR_ALL_ERRORS' }
  | { type: 'SET_GLOBAL_ERROR'; payload: { error: EnhancedApiError | null } }
  | { type: 'TOGGLE_TOASTS'; payload: { enabled: boolean } }
  | { type: 'TOGGLE_AUTO_RETRY'; payload: { enabled: boolean } }
  | { type: 'TOGGLE_ANALYTICS'; payload: { enabled: boolean } }
  | { type: 'REPORT_HEALTH'; payload: { isHealthy: boolean } }
  | { type: 'AUTO_DISMISS_ERROR'; payload: { id: string } };

// ====================================
// Error Reducer
// ====================================

const initialState: ErrorContextState = {
  errors: [],
  globalError: null,
  errorCount: 0,
  lastErrorTime: null,
  enableToasts: true,
  enableAutoRetry: true,
  enableAnalytics: true,
  isHealthy: true,
  healthCheckInterval: 30000, // 30 seconds
};

function errorReducer(state: ErrorContextState, action: ErrorAction): ErrorContextState {
  switch (action.type) {
    case 'REPORT_ERROR': {
      const { error, id, onRetry } = action.payload;

      const notification: ErrorNotification = {
        id,
        error,
        timestamp: new Date(),
        isVisible: shouldDisplayToUser(error),
        autoClose: error.severity === ErrorSeverity.LOW,
        duration: getDurationForSeverity(error.severity),
        onRetry,
      };

      return {
        ...state,
        errors: [...state.errors, notification],
        errorCount: state.errorCount + 1,
        lastErrorTime: new Date(),
        isHealthy: error.severity !== ErrorSeverity.CRITICAL,
      };
    }

    case 'DISMISS_ERROR': {
      return {
        ...state,
        errors: state.errors.filter(error => error.id !== action.payload.id),
      };
    }

    case 'RETRY_ERROR': {
      const errorNotification = state.errors.find(e => e.id === action.payload.id);
      if (errorNotification?.onRetry) {
        errorNotification.onRetry();
      }

      return {
        ...state,
        errors: state.errors.filter(error => error.id !== action.payload.id),
      };
    }

    case 'CLEAR_ALL_ERRORS': {
      return {
        ...state,
        errors: [],
        globalError: null,
      };
    }

    case 'SET_GLOBAL_ERROR': {
      return {
        ...state,
        globalError: action.payload.error,
        isHealthy: !action.payload.error || action.payload.error.severity !== ErrorSeverity.CRITICAL,
      };
    }

    case 'TOGGLE_TOASTS': {
      return {
        ...state,
        enableToasts: action.payload.enabled,
      };
    }

    case 'TOGGLE_AUTO_RETRY': {
      return {
        ...state,
        enableAutoRetry: action.payload.enabled,
      };
    }

    case 'TOGGLE_ANALYTICS': {
      return {
        ...state,
        enableAnalytics: action.payload.enabled,
      };
    }

    case 'REPORT_HEALTH': {
      return {
        ...state,
        isHealthy: action.payload.isHealthy,
      };
    }

    case 'AUTO_DISMISS_ERROR': {
      return {
        ...state,
        errors: state.errors.filter(error => error.id !== action.payload.id),
      };
    }

    default:
      return state;
  }
}

// ====================================
// Utility Functions
// ====================================

function getDurationForSeverity(severity: ErrorSeverity): number {
  switch (severity) {
    case ErrorSeverity.CRITICAL: return 0; // Manual dismiss only
    case ErrorSeverity.HIGH: return 10000; // 10 seconds
    case ErrorSeverity.MEDIUM: return 7000; // 7 seconds
    case ErrorSeverity.LOW: return 4000; // 4 seconds
    default: return 5000;
  }
}

function generateErrorId(): string {
  return `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

// ====================================
// Error Context
// ====================================

const ErrorContext = createContext<ErrorContextValue | null>(null);

// ====================================
// Error Provider Component
// ====================================

interface ErrorProviderProps {
  children: ReactNode;
  enableGlobalHandlers?: boolean;
  onCriticalError?: (error: EnhancedApiError) => void;
  onHealthChange?: (isHealthy: boolean) => void;
}

export const ErrorProvider: React.FC<ErrorProviderProps> = ({
  children,
  enableGlobalHandlers = true,
  onCriticalError,
  onHealthChange,
}) => {
  const [state, dispatch] = useReducer(errorReducer, initialState);

  // ====================================
  // Action Implementations
  // ====================================

  const reportError = useCallback((error: unknown, context?: Record<string, unknown>): string => {
    const enhancedError = handleError(error);
    const id = generateErrorId();

    // Log the error
    logError(enhancedError, {
      errorId: id,
      ...context,
    });

    // Report to analytics if enabled
    if (state.enableAnalytics) {
      reportToAnalytics(enhancedError, context);
    }

    // Check for automatic logout
    if (shouldLogoutOnError(enhancedError)) {
      handleSecurityLogout(enhancedError);
      return id;
    }

    // Handle critical errors
    if (enhancedError.severity === ErrorSeverity.CRITICAL) {
      dispatch({ type: 'SET_GLOBAL_ERROR', payload: { error: enhancedError } });
      onCriticalError?.(enhancedError);
    }

    // Create retry function if applicable
    const onRetry = enhancedError.isRetryable && state.enableAutoRetry
      ? () => {
          logger.log(`[ErrorContext] Retrying error ${id}`);
          // The actual retry logic should be implemented by the caller
        }
      : undefined;

    // Dispatch error
    dispatch({
      type: 'REPORT_ERROR',
      payload: { error: enhancedError, id, onRetry },
    });

    return id;
  }, [state.enableAnalytics, state.enableAutoRetry, onCriticalError]);

  const dismissError = useCallback((errorId: string) => {
    dispatch({ type: 'DISMISS_ERROR', payload: { id: errorId } });
  }, []);

  const retryError = useCallback((errorId: string) => {
    dispatch({ type: 'RETRY_ERROR', payload: { id: errorId } });
  }, []);

  const clearAllErrors = useCallback(() => {
    dispatch({ type: 'CLEAR_ALL_ERRORS' });
  }, []);

  const setGlobalError = useCallback((error: EnhancedApiError | null) => {
    dispatch({ type: 'SET_GLOBAL_ERROR', payload: { error } });
  }, []);

  const toggleToasts = useCallback((enabled: boolean) => {
    dispatch({ type: 'TOGGLE_TOASTS', payload: { enabled } });
  }, []);

  const toggleAutoRetry = useCallback((enabled: boolean) => {
    dispatch({ type: 'TOGGLE_AUTO_RETRY', payload: { enabled } });
  }, []);

  const toggleAnalytics = useCallback((enabled: boolean) => {
    dispatch({ type: 'TOGGLE_ANALYTICS', payload: { enabled } });
  }, []);

  const reportHealth = useCallback((isHealthy: boolean) => {
    dispatch({ type: 'REPORT_HEALTH', payload: { isHealthy } });
    onHealthChange?.(isHealthy);
  }, [onHealthChange]);

  const getErrorStats = useCallback((): ErrorStats => {
    const errorsByType: Record<string, number> = {};
    const errorsBySeverity: Record<ErrorSeverity, number> = {
      [ErrorSeverity.LOW]: 0,
      [ErrorSeverity.MEDIUM]: 0,
      [ErrorSeverity.HIGH]: 0,
      [ErrorSeverity.CRITICAL]: 0,
    };

    state.errors.forEach(errorNotification => {
      const error = errorNotification.error;
      errorsByType[error.errorType] = (errorsByType[error.errorType] || 0) + 1;
      errorsBySeverity[error.severity] = errorsBySeverity[error.severity] + 1;
    });

    const mostCommonErrors = Object.entries(errorsByType)
      .map(([type, count]) => ({ type, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    return {
      totalErrors: state.errorCount,
      errorsByType,
      errorsBySeverity,
      averageErrorsPerDay: calculateAverageErrorsPerDay(state),
      mostCommonErrors,
      healthStatus: {
        isHealthy: state.isHealthy,
        lastCheck: state.lastErrorTime,
        uptime: calculateUptime(),
      },
    };
  }, [state]);

  // ====================================
  // Effects
  // ====================================

  // Auto-dismiss errors
  useEffect(() => {
    const timeouts = state.errors
      .filter(error => error.autoClose && error.duration > 0)
      .map(error => {
        return setTimeout(() => {
          dispatch({ type: 'AUTO_DISMISS_ERROR', payload: { id: error.id } });
        }, error.duration);
      });

    return () => {
      timeouts.forEach(timeout => clearTimeout(timeout));
    };
  }, [state.errors]);

  // Global error handlers
  useEffect(() => {
    if (!enableGlobalHandlers) return;

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      logger.error('[ErrorContext] Unhandled promise rejection:', event.reason);
      reportError(event.reason, { source: 'unhandled_promise_rejection' });
      event.preventDefault();
    };

    const handleError = (event: ErrorEvent) => {
      logger.error('[ErrorContext] Global error:', event.error);
      reportError(event.error, { source: 'global_error', message: event.message });
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    window.addEventListener('error', handleError);

    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      window.removeEventListener('error', handleError);
    };
  }, [enableGlobalHandlers, reportError]);

  // Health monitoring
  useEffect(() => {
    const interval = setInterval(() => {
      // Simple health check based on recent errors
      const recentErrors = state.errors.filter(
        error => Date.now() - error.timestamp.getTime() < 60000 // Last minute
      );
      const criticalErrors = recentErrors.filter(
        error => error.error.severity === ErrorSeverity.CRITICAL
      );

      const isHealthy = criticalErrors.length === 0 && recentErrors.length < 5;

      if (isHealthy !== state.isHealthy) {
        reportHealth(isHealthy);
      }
    }, state.healthCheckInterval);

    return () => clearInterval(interval);
  }, [state.errors, state.isHealthy, state.healthCheckInterval, reportHealth]);

  // ====================================
  // Context Value
  // ====================================

  const contextValue = useMemo((): ErrorContextValue => ({
    ...state,
    reportError,
    dismissError,
    retryError,
    clearAllErrors,
    setGlobalError,
    toggleToasts,
    toggleAutoRetry,
    toggleAnalytics,
    getErrorStats,
    reportHealth,
  }), [
    state,
    reportError,
    dismissError,
    retryError,
    clearAllErrors,
    setGlobalError,
    toggleToasts,
    toggleAutoRetry,
    toggleAnalytics,
    getErrorStats,
    reportHealth,
  ]);

  return (
    <ErrorContext.Provider value={contextValue}>
      {children}
    </ErrorContext.Provider>
  );
};

// ====================================
// Hook for using Error Context
// ====================================

// eslint-disable-next-line react-refresh/only-export-components
export const useErrorContext = (): ErrorContextValue => {
  const context = useContext(ErrorContext);
  if (!context) {
    throw new Error('useErrorContext must be used within an ErrorProvider');
  }
  return context;
};

// ====================================
// Utility Hooks
// ====================================

/**
 * Hook for reporting errors with automatic context
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useErrorReporter = () => {
  const { reportError } = useErrorContext();

  return useCallback((error: unknown, additionalContext?: Record<string, unknown>) => {
    const context = {
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      ...additionalContext,
    };

    return reportError(error, context);
  }, [reportError]);
};

/**
 * Hook for handling errors in async operations
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useAsyncErrorHandler = () => {
  const reportError = useErrorReporter();

  return useCallback(<T extends (...args: unknown[]) => Promise<unknown>>(
    asyncFn: T,
    context?: Record<string, unknown>
  ) => {
    return ((...args: Parameters<T>) => {
      return asyncFn(...args).catch((error) => {
        reportError(error, { source: 'async_operation', ...context });
        throw error; // Re-throw to allow caller to handle
      });
    }) as T;
  }, [reportError]);
};

// ====================================
// Helper Functions
// ====================================

function calculateAverageErrorsPerDay(state: ErrorContextState): number {
  if (!state.lastErrorTime) return 0;

  const daysSinceFirstError = Math.max(
    1,
    (Date.now() - state.lastErrorTime.getTime()) / (1000 * 60 * 60 * 24)
  );

  return state.errorCount / daysSinceFirstError;
}

function calculateUptime(): number {
  const startTime = parseInt(sessionStorage.getItem('app_start_time') || Date.now().toString());
  return Date.now() - startTime;
}

function reportToAnalytics(error: EnhancedApiError, context?: Record<string, unknown>): void {
  // Analytics implementation would go here
  // For now, just log for development
  if (import.meta.env?.MODE === 'development') {
    logger.log('[Analytics] Error reported:', {
      errorType: error.errorType,
      severity: error.severity,
      statusCode: error.statusCode,
      context,
    });
  }
}

function handleSecurityLogout(error: EnhancedApiError): void {
  logger.warn('[ErrorContext] Security logout triggered', {
    errorType: error.errorType,
    severity: error.severity,
  });

  // Clear all authentication data
  try {
    localStorage.clear();
    sessionStorage.clear();

    // Redirect to login page
    window.location.href = '/login?reason=security';
  } catch (clearError) {
    logger.error('[ErrorContext] Failed to clear storage during security logout', clearError);
  }
}

// Set app start time for uptime calculation
if (typeof window !== 'undefined' && !sessionStorage.getItem('app_start_time')) {
  sessionStorage.setItem('app_start_time', Date.now().toString());
}

export default ErrorProvider;